import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Custom hook to track user session and screen time
 * @param {string} userEmail - Current user's email
 * @param {string} currentTicket - Currently viewing ticket ID (optional)
 * @returns {Object} Session statistics
 */
export const useSessionTracking = (userEmail, currentTicket = null) => {
  const sessionIdRef = useRef(null);
  const userEmailRef = useRef(null); // Store email in ref to persist during logout
  const ticketStartTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const [sessionStats, setSessionStats] = useState({
    totalTime: 0,
    activeTime: 0,
    ticketsViewed: 0
  });

  // Update email ref when it changes
  useEffect(() => {
    if (userEmail) {
      userEmailRef.current = userEmail;
    }
  }, [userEmail]);

  // Initialize session on mount
  useEffect(() => {
    if (!userEmail) {
      console.log('[Session Tracking] No userEmail provided, skipping session tracking');
      return;
    }

    const startSession = async () => {
      try {
        // Generate unique session ID
        sessionIdRef.current = uuidv4();
        
        console.log('[Session Tracking] Starting session for:', userEmail);
        console.log('[Session Tracking] Session ID:', sessionIdRef.current);

        const response = await fetch('/api/session/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            userName: userEmail.split('@')[0], // Extract username from email
            sessionId: sessionIdRef.current,
            userAgent: navigator.userAgent,
            ipAddress: '' // Backend can capture from request
          })
        });

        const result = await response.json();
        console.log('[Session Tracking] Session started:', result);
        
        if (!result.success) {
          console.error('[Session Tracking] Failed to start session:', result);
          return;
        }

        // Start heartbeat - update activity every 30 seconds
        intervalRef.current = setInterval(() => {
          sendHeartbeat();
        }, 30000); // 30 seconds
        
        console.log('[Session Tracking] Heartbeat started (30s interval)');

      } catch (error) {
        console.error('[Session Tracking] Error starting session:', error);
      }
    };

    startSession();

    // End session on unmount or page unload
    const handleUnload = () => {
      console.log('[Session Tracking] Ending session');
      endSession();
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      handleUnload();
      window.removeEventListener('beforeunload', handleUnload);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [userEmail]);

  // Track ticket viewing time
  useEffect(() => {
    if (currentTicket) {
      // User opened a ticket - start timer
      ticketStartTimeRef.current = Date.now();
    } else {
      // User left ticket view - send update
      if (ticketStartTimeRef.current) {
        const timeSpent = Math.floor((Date.now() - ticketStartTimeRef.current) / 1000);
        ticketStartTimeRef.current = null;
      }
    }
  }, [currentTicket]);

  const sendHeartbeat = async () => {
    if (!sessionIdRef.current || !userEmail) {
      console.log('[Session Tracking] Skipping heartbeat - no session or user');
      return;
    }

    try {
      // Calculate time spent on current ticket
      let ticketTime = 0;
      if (currentTicket && ticketStartTimeRef.current) {
        ticketTime = Math.floor((Date.now() - ticketStartTimeRef.current) / 1000);
      }
      
      console.log('[Session Tracking] Sending heartbeat - Ticket:', currentTicket, 'Time:', ticketTime);

      const response = await fetch('/api/session/activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          userEmail,
          currentTicket,
          ticketTime
        })
      });

      const result = await response.json();
      console.log('[Session Tracking] Heartbeat response:', result);
      
      if (result.success && result.totalDuration) {
        setSessionStats(prev => ({
          ...prev,
          totalTime: result.totalDuration
        }));
      }
    } catch (error) {
      console.error('[Session Tracking] Error sending heartbeat:', error);
    }
  };

  const endSession = async () => {
    // Use ref to get email even if prop is null (during logout)
    const emailToUse = userEmailRef.current;
    
    if (!sessionIdRef.current || !emailToUse) {
      console.log('[Session Tracking] Cannot end session - no session ID or user email');
      return;
    }

    try {
      console.log('[Session Tracking] Ending session:', sessionIdRef.current, 'for user:', emailToUse);
      
      const data = {
        sessionId: sessionIdRef.current,
        userEmail: emailToUse
      };

      // Use fetch with keepalive instead of sendBeacon for better reliability
      // sendBeacon doesn't set Content-Type header properly
      const response = await fetch('/api/session/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true // Ensures request completes even if page is closing
      });

      const result = await response.json();
      console.log('[Session Tracking] Session ended successfully:', result);
      
    } catch (error) {
      console.error('[Session Tracking] Error ending session:', error);
    }
  };

  return {
    sessionId: sessionIdRef.current,
    sessionStats,
    sendHeartbeat, // Expose for manual trigger if needed
    endSession // Expose for manual session end
  };
};

export default useSessionTracking;
